import { create } from 'zustand';

import { generateConfigFromEnum } from '@src/utils';

import {
  IMainConfig,
  IChatConfig,
  IPollsConfig,
  ISettingsConfig,
  IQuestionsConfig,
  IParticipantsListConfig,
  IMagicPenConfig,
  IUpdateRoomConfigResponse,
  EMainConfigKeys,
  EChatConfigKeys,
  EPollsConfigKeys,
  ESettingsConfigKeys,
  EQuestionsConfigKeys,
  EParticipantsListConfigKeys,
  EMagicPenConfigKeys,
  ICalculatorConfig,
  ECalculatorConfigKeys,
} from '@src/types';

interface IRoomConfigStore {
  // Feature configs
  mainConfig: IMainConfig;
  setMainConfig: (config: Partial<IMainConfig>) => void;

  chatConfig: IChatConfig;
  setChatConfig: (config: Partial<IChatConfig>) => void;

  pollConfig: IPollsConfig;
  setPollConfig: (config: Partial<IPollsConfig>) => void;

  settingsConfig: ISettingsConfig;
  setSettingsConfig: (config: Partial<ISettingsConfig>) => void;

  questionsConfig: IQuestionsConfig;
  setQuestionsConfig: (config: Partial<IQuestionsConfig>) => void;

  participantsListConfig: IParticipantsListConfig;
  setParticipantsListConfig: (config: Partial<IParticipantsListConfig>) => void;

  magicPenConfig: IMagicPenConfig;
  setMagicPenConfig: (config: Partial<IMagicPenConfig>) => void;

  calculatorConfig: ICalculatorConfig;
  setCalculatorConfig: (config: Partial<ICalculatorConfig>) => void;

  // Configs to show the state of the settings (this reflects the settings regarding the affected users)
  // This is used mainly for the switch/toggle UI state without affecting the RBAC actual behavior
  userTypeRoomConfig: {
    main: IMainConfig;
    chat: IChatConfig;
    calculator: ICalculatorConfig;
  };

  setUserTypeRoomConfig: (config: Partial<IUpdateRoomConfigResponse>) => void;
}

export const useRoomConfigStore = create<IRoomConfigStore>((set, get) => ({
  mainConfig: generateConfigFromEnum(EMainConfigKeys),
  setMainConfig: (config) => set((state) => ({ mainConfig: { ...state.mainConfig, ...config } })),

  chatConfig: generateConfigFromEnum(EChatConfigKeys),
  setChatConfig: (config) => set((state) => ({ chatConfig: { ...state.chatConfig, ...config } })),

  pollConfig: generateConfigFromEnum(EPollsConfigKeys),
  setPollConfig: (config) => set((state) => ({ pollConfig: { ...state.pollConfig, ...config } })),

  settingsConfig: generateConfigFromEnum(ESettingsConfigKeys),
  setSettingsConfig: (config) => set((state) => ({ settingsConfig: { ...state.settingsConfig, ...config } })),

  questionsConfig: generateConfigFromEnum(EQuestionsConfigKeys),
  setQuestionsConfig: (config) => set((state) => ({ questionsConfig: { ...state.questionsConfig, ...config } })),

  participantsListConfig: generateConfigFromEnum(EParticipantsListConfigKeys),
  setParticipantsListConfig: (config) =>
    set((state) => ({ participantsListConfig: { ...state.participantsListConfig, ...config } })),

  magicPenConfig: generateConfigFromEnum(EMagicPenConfigKeys),
  setMagicPenConfig: (config) => set((state) => ({ magicPenConfig: { ...state.magicPenConfig, ...config } })),

  calculatorConfig: generateConfigFromEnum(ECalculatorConfigKeys),
  setCalculatorConfig: (config) => set((state) => ({ calculatorConfig: { ...state.calculatorConfig, ...config } })),

  userTypeRoomConfig: {
    main: generateConfigFromEnum(EMainConfigKeys),
    chat: generateConfigFromEnum(EChatConfigKeys),
    calculator: generateConfigFromEnum(ECalculatorConfigKeys),
  },
  setUserTypeRoomConfig: (config) =>
    set((state) => ({
      userTypeRoomConfig: {
        main: { ...state.userTypeRoomConfig.main, ...(config.roomConfig?.main || {}) },
        chat: { ...state.userTypeRoomConfig.chat, ...(config.roomConfig?.chat || {}) },
        calculator: { ...state.userTypeRoomConfig.calculator, ...(config.roomConfig?.calculator || {}) },
      },
    })),
}));
